import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRightIcon, CalendarIcon, CheckCircle2Icon, ClockIcon, MailIcon, MessageSquareIcon, PhoneIcon, UserIcon, BoxIcon } from "lucide-react";
import { COMPANY } from "../data/company";
/** Identificadores públicos de EmailJS (frontend). No se usa Private Key. */
const EMAILJS_ENDPOINT = 'https://api.emailjs.com/api/v1.0/email/send';
const EMAILJS_SERVICE_ID = 'service_bk8btlt';
const EMAILJS_TEMPLATE_ID = 'template_sr4kx2w';
const EMAILJS_PUBLIC_KEY = '_NVWkcAhl13YfsJE-';
interface ContactStep {
  icon: BoxIcon;
  title: string;
  description: string;
}
const contactSteps: ContactStep[] = [{
  icon: ClockIcon,
  title: 'Revisamos tu mensaje',
  description: 'Leemos tu contexto y detectamos los puntos que conviene abordar primero.'
}, {
  icon: MessageSquareIcon,
  title: 'Definimos la conversación',
  description: 'Te respondemos para entender prioridades, materiales y alcance.'
}, {
  icon: CalendarIcon,
  title: 'Aterrizamos el siguiente paso',
  description: 'Si hay encaje, acordamos la forma más clara de avanzar.'
}];
const sectors = [{
  value: 'hosteleria',
  label: 'Hostelería'
}, {
  value: 'servicios',
  label: 'Servicios locales'
}, {
  value: 'comercio',
  label: 'Comercio'
}, {
  value: 'profesional',
  label: 'Marca personal o profesional'
}, {
  value: 'otro',
  label: 'Otro'
}];
/** Campos homogéneos: misma altura, mismo borde, mismo estado de foco. */
const FIELD_CLASS = 'h-[52px] w-full border border-white/20 bg-black px-4 text-sm text-white transition-colors placeholder:text-neutral-500 hover:border-white/35 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]';
const LABEL_CLASS = 'mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-300';
const EMPTY_FORM = {
  name: '',
  business: '',
  email: '',
  phone: '',
  service: '',
  message: ''
};
export function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState(EMPTY_FORM);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const isValid = Boolean(formData.name.trim()) && Boolean(formData.business.trim()) && /^\S+@\S+\.\S+$/.test(formData.email.trim()) && Boolean(formData.phone.trim()) && Boolean(formData.service);
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (formStatus === 'submitting' || !isValid) return;
    setFormStatus('submitting');
    const templateParams: Record<string, string> = {
      full_name: String(formData.name.trim()),
      business_name: String(formData.business.trim()),
      email: String(formData.email.trim()),
      phone: String(formData.phone.trim()),
      sector: String(sectors.find(sector => sector.value === formData.service)?.label || formData.service || ''),
      project_details: String(formData.message.trim() || 'Sin detalles adicionales')
    };
    try {
      // Llamada directa al endpoint REST oficial de EmailJS: es exactamente la
      // misma petición que realiza el SDK, sin depender de su versión.
      const response = await fetch(EMAILJS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_id: EMAILJS_SERVICE_ID,
          template_id: EMAILJS_TEMPLATE_ID,
          user_id: EMAILJS_PUBLIC_KEY,
          template_params: templateParams
        })
      });
      const responseText = await response.text();
      if (!response.ok) {
        // Diagnóstico técnico en consola; el usuario solo ve el mensaje genérico.
        console.error('[EmailJS] status:', response.status);
        console.error('[EmailJS] text:', responseText);
        console.error('[EmailJS] endpoint:', EMAILJS_ENDPOINT);
        console.error('[EmailJS] params enviados:', templateParams);
        setFormStatus('error');
        return;
      }
      setFormData(EMPTY_FORM);
      setFormStatus('success');
    } catch (error) {
      console.error('[EmailJS] error:', error);
      console.error('[EmailJS] status:', (error as {
        status?: number;
      })?.status);
      console.error('[EmailJS] text:', (error as {
        text?: string;
      })?.text);
      console.error('[EmailJS] message:', (error as {
        message?: string;
      })?.message);
      setFormStatus('error');
    }
  };
  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData(current => ({
      ...current,
      [field]: value
    }));
    setFormStatus(current => current === 'idle' || current === 'submitting' ? current : 'idle');
  };
  return <div className="min-h-screen bg-[#0a0a0a] pt-20 sm:pt-24" data-id="element-258" data-mp="25j4poa3buq60">
      <section className="page-header" data-id="element-259" data-mp="2aj966048jkw8">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-260" data-mp="2akah84l947dy">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-261" data-mp="2fh0w4olbkwxs">
            <div data-id="element-262" data-mp="2fmt8do7ka1hf">
              <p className="eyebrow" data-id="element-263" data-mp="2focpfh11kgk9">Contacto</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-5xl lg:text-6xl" data-id="element-264" data-mp="2blzkguu4cmoj">
                Cuéntanos qué necesita tu negocio ahora.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg" data-id="element-265" data-mp="33d5mxhxbawgz">
              Completa el formulario y utilizaremos la información para entender
              el punto de partida de tu proyecto. Sin mensajes automatizados ni
              compromisos innecesarios.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell" data-id="element-266" data-mp="3r4b729aacax3">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-267" data-mp="3mc9wqswogu9p">
          <div className="grid overflow-hidden border border-white/10 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-268" data-mp="3m9x3hjlgyxhc">
            <aside className="flex flex-col bg-black p-7 sm:p-10 lg:border-r lg:border-white/10" data-id="element-269" data-mp="1kgkia2743nhd">
              <p className="eyebrow" data-id="element-270" data-mp="1pfnbmpdqc59i">Después de escribirnos</p>
              <h2 className="mt-5 text-2xl font-medium tracking-[-0.035em] text-white" data-id="element-271" data-mp="1anmtjebj12ab">
                Una primera conversación con contexto.
              </h2>
              <div className="mt-9 space-y-7" data-id="element-272" data-mp="11o9lmq31wlrp">
                {contactSteps.map(step => {
                const StepIcon = step.icon;
                return <div key={step.title} className="flex gap-4" data-id="element-273" data-mp="16nujex6hio2u">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/20 text-white" data-id="element-274" data-mp="rqbw7z9ggixi">
                        <StepIcon size={16} aria-hidden="true" data-id="element-275" data-mp="wpojum85hu15" />
                      </span>
                      <div data-id="element-276" data-mp="2m4wgiatv6cgd">
                        <h3 className="text-sm font-medium text-white" data-id="element-277" data-mp="2m4ml27q1vd3a">
                          {step.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-neutral-300" data-id="element-278" data-mp="ffb58kzrtoug">
                          {step.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
              <div className="mt-10 border-t border-white/10 pt-6 lg:mt-auto" data-id="element-279" data-mp="p6ytkd116prp">
                <p className="meta-label" data-id="element-280" data-mp="kdmh119mm9fb">Soporte directo</p>
                <ul className="mt-4 space-y-3" data-id="element-281" data-mp="y62apbgn0v3k">
                  <li data-id="element-282" data-mp="y4r83eyxhte1">
                    <a href={`mailto:${COMPANY.supportEmail}`} className="inline-flex items-center gap-3 text-sm text-white underline underline-offset-4 transition-colors hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-283" data-mp="y85cstv2eeon">
                      <MailIcon size={15} className="shrink-0" aria-hidden="true" data-id="element-284" data-mp="1366upbgr87yj" />
                      {COMPANY.supportEmail}
                    </a>
                  </li>
                  <li data-id="element-285" data-mp="34ws4srwy8m5d">
                    <a href={COMPANY.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-sm text-white underline underline-offset-4 transition-colors hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-286" data-mp="34xjzm0iq2ful">
                      <PhoneIcon size={15} className="shrink-0" aria-hidden="true" data-id="element-287" data-mp="2ssw7fiudzmn6" />
                      {COMPANY.phone}
                    </a>
                  </li>
                </ul>
                <p className="mt-4 text-xs leading-relaxed text-neutral-300" data-id="element-288" data-mp="2050aqe4thuqe">
                  Atendemos consultas antes y después de la compra. Respondemos en un plazo máximo de 3 días hábiles.
                </p>
                <p className="mt-5 flex items-center gap-2 text-xs font-medium text-neutral-200" data-id="element-289" data-mp="2a1mwd54ywpth">
                  <CheckCircle2Icon size={15} className="shrink-0 text-white" aria-hidden="true" data-id="element-290" data-mp="2f2spv56gch42" />
                  Tus datos se tratan de forma confidencial.
                </p>
              </div>
            </aside>

            <motion.form initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.28,
            delay: 0.06,
            ease: [0.23, 1, 0.32, 1]
          }} onSubmit={handleSubmit} className="bg-[#0a0a0a] p-7 sm:p-10" data-id="element-291" data-mp="3qfd1mf7tpk4p">
              <div className="grid gap-5 md:grid-cols-2" data-id="element-292" data-mp="151ex40611rrz">
                <label className="block" data-id="element-293" data-mp="103vj91d8rh0k">
                  <span className={LABEL_CLASS} data-id="element-294" data-mp="v5lmimbvc8cu">Nombre completo *</span>
                  <span className="relative block" data-id="element-295" data-mp="v6wruqkypo62">
                    <UserIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-296" data-mp="r3gshmhid9po" />
                    <input required value={formData.name} onChange={event => updateField('name', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="Tu nombre" autoComplete="name" data-id="element-297" data-mp="m6zvs3w8tn0z" />
                  </span>
                </label>
                <label className="block" data-id="element-298" data-mp="2bjbva6dxnkst">
                  <span className={LABEL_CLASS} data-id="element-299" data-mp="2bkmvjw866af6">Negocio *</span>
                  <input required value={formData.business} onChange={event => updateField('business', event.target.value)} className={FIELD_CLASS} placeholder="Nombre de tu negocio" autoComplete="organization" data-id="element-300" data-mp="xelwmifso7se" />
                </label>
                <label className="block" data-id="element-301" data-mp="16fabtggxce6v">
                  <span className={LABEL_CLASS} data-id="element-302" data-mp="12erj7k77j43m">Correo electrónico *</span>
                  <span className="relative block" data-id="element-303" data-mp="12cng34h4u94e">
                    <MailIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-304" data-mp="epysq39ctoge" />
                    <input required type="email" value={formData.email} onChange={event => updateField('email', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="tu@email.com" autoComplete="email" data-id="element-305" data-mp="9nzrp01373mm" />
                  </span>
                </label>
                <label className="block" data-id="element-306" data-mp="3r4rde0hlesnu">
                  <span className={LABEL_CLASS} data-id="element-307" data-mp="3n4pc3sfrs193">Teléfono *</span>
                  <span className="relative block" data-id="element-308" data-mp="3n4gw3rocofto">
                    <PhoneIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-309" data-mp="3d08xf2e10q6v" />
                    <input required type="tel" value={formData.phone} onChange={event => updateField('phone', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="+34 600 000 000" autoComplete="tel" data-id="element-310" data-mp="3htlgyy3um6sr" />
                  </span>
                </label>
              </div>
              <label className="mt-5 block" data-id="element-311" data-mp="3vrt8urm12hx2">
                <span className={LABEL_CLASS} data-id="element-312" data-mp="4kp5q7t07ftx">Sector o actividad *</span>
                <select required value={formData.service} onChange={event => updateField('service', event.target.value)} className={`${FIELD_CLASS} appearance-none bg-[url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")] bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-11 ${formData.service ? 'text-white' : 'text-neutral-500'}`} data-id="element-313" data-mp="4iw5pbcwbtuw">
                  <option value="" disabled data-id="element-314" data-mp="ltrrh49h7xoa">
                    Selecciona una opción
                  </option>
                  {sectors.map(sector => <option key={sector.value} value={sector.value} data-id="element-315" data-mp="c4hvhdbihuuw">
                      {sector.label}
                    </option>)}
                </select>
              </label>
              <label className="mt-5 block" data-id="element-316" data-mp="73aohgh70zpa">
                <span className={LABEL_CLASS} data-id="element-317" data-mp="26k9yxl1ec5z">
                  Sobre el proyecto{' '}
                  <span className="normal-case tracking-normal text-neutral-400" data-id="element-318" data-mp="3u9l7lhzwb74t">
                    (opcional)
                  </span>
                </span>
                <textarea rows={5} value={formData.message} onChange={event => updateField('message', event.target.value)} className={`${FIELD_CLASS} h-auto resize-none py-3.5 leading-relaxed`} placeholder="Qué quieres mejorar, qué necesitas ordenar o qué objetivo tienes en mente." data-id="element-319" data-mp="3pd485rn8x6cf" />
              </label>
              <button type="submit" disabled={formStatus === 'submitting'} className="mt-7 flex h-[52px] w-full items-center justify-center gap-2 bg-white px-5 text-sm font-semibold text-black transition-colors hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-320" data-mp="1iiqykfybqtew">
                {formStatus === 'submitting' ? <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/25 border-t-black" aria-hidden="true" data-id="element-321" data-mp="176ifq4zq4kx1" />
                    Enviando...
                  </> : <>
                    Enviar consulta{' '}
                    <ArrowRightIcon size={16} aria-hidden="true" data-id="element-322" data-mp="1h5ra2yyi41jx" />
                  </>}
              </button>
              {formStatus === 'success' && <p role="status" className="mt-4 border border-white/25 px-4 py-3 text-sm leading-relaxed text-white" data-id="element-323" data-mp="2sjcwiccz7p1r">
                  Solicitud enviada correctamente. Hemos recibido tu información
                  y nos pondremos en contacto contigo.
                </p>}
              {formStatus === 'error' && <p role="alert" className="mt-4 border border-red-500/60 px-4 py-3 text-sm leading-relaxed text-red-300" data-id="element-324" data-mp="2r265x63y6yqp">
                  No hemos podido enviar tu solicitud. Inténtalo de nuevo.
                </p>}
              <p className="mt-4 text-center text-xs leading-relaxed text-neutral-400" data-id="element-325" data-mp="3011eupydmmxp">
                Al enviar, aceptas que utilicemos esta información para
                responder a tu consulta.
              </p>
            </motion.form>
          </div>
        </div>
      </section>
    </div>;
}